import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Video, Calendar, Radio } from "lucide-react";

export const Route = createFileRoute("/learner/live")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Live Lessons — MAKAI" }] }),
  component: LearnerLivePage,
});

type Row = { id: string; title: string; description: string | null; scheduled_start: string; scheduled_end: string; status: string; recording_resource_id: string | null; recording_storage_path: string | null };

function LearnerLivePage() {
  const { ctx, loading } = useLearnerContext();
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (!ctx?.streamId) return;
    (async () => {
      const { data } = await supabase.from("live_lessons")
        .select("id, title, description, scheduled_start, scheduled_end, status, recording_resource_id, recording_storage_path")
        .eq("stream_id", ctx.streamId!)
        .neq("status", "cancelled")
        .order("scheduled_start", { ascending: true });
      setRows((data ?? []) as Row[]);
    })();
  }, [ctx?.streamId]);

  if (loading) return <AppShell role="learner" title="Live Lessons"><p className="text-sm text-muted-foreground">Loading…</p></AppShell>;

  const now = Date.now();
  const upcoming = rows.filter((r) => new Date(r.scheduled_end).getTime() > now);
  const past = rows.filter((r) => new Date(r.scheduled_end).getTime() <= now).slice(0, 10);

  return (
    <AppShell role="learner" title="Live Lessons" subtitle="Join your teacher's video classroom">
      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Upcoming</h2>
        {upcoming.length === 0 ? (
          <Card className="grid place-items-center p-8 text-sm text-muted-foreground">
            <Video className="mb-2 h-8 w-8 opacity-40" /> No live lessons scheduled.
          </Card>
        ) : upcoming.map((l) => <LessonRow key={l.id} l={l} />)}
      </section>
      {past.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Past</h2>
          {past.map((l) => <LessonRow key={l.id} l={l} />)}
        </section>
      )}
    </AppShell>
  );
}

function LessonRow({ l }: { l: Row }) {
  const start = new Date(l.scheduled_start);
  const end = new Date(l.scheduled_end);
  const now = Date.now();
  const canJoin = now >= start.getTime() - 10 * 60_000 && now <= end.getTime() + 30 * 60_000;
  return (
    <Card className="mb-3 grid gap-3 p-4 md:grid-cols-[1fr_auto] md:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-semibold">{l.title}</h3>
          {l.status === "live" && <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[11px] font-semibold text-destructive"><Radio className="h-3 w-3 animate-pulse" /> LIVE</span>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" /> {start.toLocaleString()} – {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
        {l.description && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{l.description}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {l.recording_storage_path && (
          <Button size="sm" variant="outline" className="gap-1" onClick={async () => {
            const { signedUrl } = await import("@/lib/uploads");
            const url = await signedUrl("library-files", l.recording_storage_path!, 3600);
            if (url) window.open(url, "_blank", "noopener");
          }}>
            <Video className="h-4 w-4" /> Recording
          </Button>
        )}
        <Button asChild disabled={!canJoin} className="gap-1">
          <Link to="/learner/live/$id" params={{ id: l.id }}><Video className="h-4 w-4" /> {canJoin ? "Join" : "Not yet"}</Link>
        </Button>
      </div>
    </Card>
  );
}
